// eslint-disable-next-line import/no-cycle
import { adminApi } from '../includes/contentManagementApi';
// eslint-disable-next-line import/no-cycle
import { mainApi } from '../includes/mainApi';

export default {
  /**
   * @returns {Promise<Array>} - List of users
   * @description Fetches all users from the server.
   * @throws {Error} - If the request fails
   */
  async fetchUsers() {
    const res = await adminApi.get('/user');
    return res.data;
  },

  /**
   * @param {*} userId
   * @param {*} roles
   * @returns {Promise<Object>} - Updated user data
   */
  async updateUser(userId, roles) {
    return adminApi.put(`/user/${userId}`, { roles });
  },

  /**
   * @param {string} userId - User ID
   * @param {boolean} isActivated - Whether to activate reflection guidelines
   * @returns {Promise<Object>} - Updated user data
   */
  async updateReflexionGuidelineActivated(userId, isActivated) {
    return adminApi.put(`/user/${userId}`, { reflexionGuidelineActivated: isActivated });
  },

  /**
   * @param {string} userId - User ID
   * @param {boolean} activated - Whether to activate travel buddy for the user
   * @returns {Promise<Object>} - Updated user data
   */
  async updateTravelBuddyActivated(userId, activated) {
    return adminApi.put(`/user/${userId}`, { travelBuddyActivated: activated });
  },

  /**
   * @returns {Promise<Object>} - User's DevTip library
   * @description Fetches the current user's DevTip library from the server.
   * @throws {Error} - If the request fails
   */
  async getDevTipLibrary() {
    const res = await adminApi.get('/user/devtip-library');
    return res.data;
  },

  /**
   * @param {Object} data - Feedback data containing id and feedback object
   * @returns {Promise<Object>} - Feedback submission result
   * @description Submits feedback for a DevTip.
   * @throws {Error} - If the request fails
   */
  async submitDevTipFeedback(data) {
    const res = await adminApi.post('/user/devtip-feedback', data);
    return res.data;
  },

  /**
   * Set external evaluation enabled status for users (bulk operation)
   * @param {Object} data - Object containing setTo boolean and optional userIds array
   * @returns {Promise<Object>} - Response with success status and affected count
   */
  async setExternalEvaluationEnabled(data) {
    const res = await adminApi.post('/user/setExternalEvaluationEnabled', data);
    return res.data;
  },

  /**
   * Enable or disable external evaluation schemas for users (bulk).
   * @param {Object} data - { action: 'enable'|'disable', schemaIds: string[], userIds?: string[] }
   */
  async setExternalEvaluationSchemas(data) {
    const res = await adminApi.post('/user/setExternalEvaluationSchemas', data);
    return res.data;
  },

  /**
   * Update per-user enabled external evaluation schema IDs.
   * Clearing all schemas clears `enabledExternalEvaluationSchemas` (master flag is derived on read).
   */
  async updateEnabledExternalEvaluationSchemas(userId, schemaIds) {
    const normalizedIds = schemaIds || [];
    if (normalizedIds.length === 0) {
      return this.setExternalEvaluationEnabled({ userIds: [userId], setTo: false });
    }
    const res = await adminApi.put(`/user/${userId}`, {
      externalEvaluationSchemaIds: normalizedIds,
    });
    return res.data;
  },

  /**
   * Admin: per-schema eligibility for a user.
   */
  async getExternalEvaluationEligibility(userId) {
    const res = await adminApi.get(`/user-overview/${userId}/external-evaluation-eligibility`);
    return res.data;
  },

  /**
   * Generate external evaluation test data for a single user (calls old backend)
   * @param {string} userId - User ID to generate test data for
   * @returns {Promise<Object>} - Response with success status and generated data summary
   */
  async generateTestData(userId) {
    const res = await mainApi.post('/admin/commands/generateExternalEvaluationTestData', {
      userId,
      assessmentType: 'mindset360',
    });
    return res.data;
  },

  /**
   * @param {string} userId - User ID
   * @param {string} section - Section to fetch (profile, devtips, travel-buddy, assessments, levels, kss, personal-goals, audit)
   * @returns {Promise<Object>} - Section data for the user
   */
  async getUserOverview(userId, section) {
    const res = await adminApi.get(`/user-overview/${userId}/${section}`);
    return res.data;
  },

  /**
   * Admin-only CSV: email, name, roles, permission flags
   */
  async exportPermissionsCsv() {
    return adminApi.post('/user/export-permissions-csv', {}, { responseType: 'blob' });
  },
};
